import React, { useState } from 'react';
import { Link, usePage } from '@inertiajs/react';
import MobileNavLink from '@/Components/Navigation/MobileNavLink';
import classNames from 'classnames';
import useRoute from '@/Hooks/useRoute';
import { router } from '@inertiajs/react';
import AppLogo from '../Components/Utility/AppLogo';
import { motion, AnimatePresence } from 'framer-motion';

const HeaderLayout = ({ title }) => {
  const page = usePage();
  const route = useRoute();

  const [showingNavigationDropdown, setShowingNavigationDropdown] =
    useState(false);

  function logout(e) {
    e.preventDefault();
    router.post(route('logout'));
  }

  const animateMobileDropdown = {
    initial: { height: 0 },
    animate: { height: 'fit-content' },
    exit: { height: 0, transition: { duration: 0.1 } },
  };

  return (
    <header className="mb-4">
      <div className="fixed top-0 left-0 z-front flex h-fit flex-col justify-center border-b-width-mini border-grey-200 bg-white md:relative lg:h-20">
        <div className="flex w-screen items-center justify-between lg:w-full">
          <div className="flex items-center justify-start">
            <Link href={route('links.show')}>
              <div className="w-20 lg:hidden">
                <AppLogo />
              </div>
            </Link>
            <h5 className="ml-2 px-6 text-2xl font-medium text-dark">
              {title}
            </h5>
          </div>
          {/* <!-- Hamburger --> */}
          <button
            onClick={() =>
              setShowingNavigationDropdown(!showingNavigationDropdown)
            }
            className="mx-2 flex items-center justify-center rounded-md p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-500 focus:bg-gray-100 focus:text-gray-500 focus:outline-none lg:hidden"
          >
            <svg
              className="h-8 w-8"
              stroke="currentColor"
              fill="fill-primary"
              viewBox="0 0 24 24"
            >
              <path
                className={classNames({
                  hidden: showingNavigationDropdown,
                  'inline-flex': !showingNavigationDropdown,
                })}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
              <path
                className={classNames({
                  hidden: !showingNavigationDropdown,
                  'inline-flex': showingNavigationDropdown,
                })}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <AnimatePresence mode="wait">
          {showingNavigationDropdown && (
            <motion.div
              className="z-front flex h-fit flex-col items-center justify-between overflow-hidden rounded-b-card bg-slate-800 md:hidden"
              variants={animateMobileDropdown}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {/* <!-- Responsive Navigation Menu --> */}
              <div className="w-full">
                <div className="mx-4 my-4 flex items-center">
                  {page.props.jetstream.managesProfilePhotos ? (
                    <div className="mr-3 flex-shrink-0">
                      <img
                        className="h-10 w-10 rounded-full object-cover"
                        src={page.props.user.profile_photo_url}
                        alt={page.props.user.name}
                      />
                    </div>
                  ) : null}
                  <div>
                    <div className="text-base font-medium text-white">
                      {page.props.user.name}
                    </div>
                    <div className="text-sm font-medium text-light">
                      {page.props.user.email}
                    </div>
                  </div>
                </div>
                {/* <!-- Responsive Settings Options --> */}
                <div className="border-t border-slate-700">
                  <MobileNavLink
                    href={route('links.show')}
                    active={route().current('links.show')}
                  >
                    <div className="flex items-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M16.959 3.04106C15.8201 1.90229 13.9739 1.90229 12.8352 3.04106L10 5.87617C9.57295 6.30322 8.88055 6.30322 8.45351 5.87617C8.02661 5.44914 8.02661 4.75679 8.45351 4.32974L11.2887 1.49462C13.2815 -0.498201 16.5125 -0.498215 18.5053 1.49462C20.4982 3.48746 20.4982 6.71847 18.5053 8.71126L15.6703 11.5465C15.2432 11.9735 14.5508 11.9735 14.1238 11.5465C13.6967 11.1194 13.6967 10.427 14.1238 9.99998L16.959 7.16492C18.0977 6.02611 18.0977 4.17982 16.959 3.04106Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.04106 16.9589C1.9023 15.8201 1.9023 13.9738 3.04106 12.835L5.87618 9.99995C6.30323 9.57291 6.30323 8.88051 5.87618 8.45346C5.44915 8.02642 4.75679 8.02642 4.32975 8.45346L1.49463 11.2887C-0.49821 13.2814 -0.49821 16.5125 1.49463 18.5053C3.48745 20.4982 6.71848 20.4982 8.71127 18.5053L11.5465 15.6702C11.9735 15.2432 11.9735 14.5508 11.5465 14.1237C11.1194 13.6967 10.427 13.6967 9.99999 14.1237L7.16493 16.9589C6.02612 18.0976 4.17982 18.0976 3.04106 16.9589Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M13.6083 6.39168C14.0354 6.81871 14.0354 7.51101 13.6083 7.93806L7.93807 13.6083C7.51102 14.0354 6.81867 14.0354 6.39162 13.6083C5.96459 13.1813 5.96459 12.489 6.39162 12.062L12.0618 6.39168C12.4889 5.96465 13.1813 5.96465 13.6083 6.39168Z"
                          fill="white"
                        />
                      </svg>
                      <span className="ml-2">Links</span>
                    </div>
                  </MobileNavLink>
                  <MobileNavLink
                    href={route('profile.show')}
                    active={route().current('profile.show')}
                  >
                    <div className="flex items-center">
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.0197 4.88561C9.39458 4.88561 7.24697 7.03322 7.24697 9.65833C7.24697 12.2834 9.39458 14.431 12.0197 14.431C14.6448 14.431 16.7924 12.2834 16.7924 9.65833C16.7924 7.03322 14.6448 4.88561 12.0197 4.88561ZM12.0197 12.7568C10.3156 12.7568 8.92121 11.3624 8.92121 9.65833C8.92121 7.95428 10.3156 6.55985 12.0197 6.55985C13.7237 6.55985 15.1182 7.95428 15.1182 9.65833C15.1182 11.3624 13.7237 12.7568 12.0197 12.7568Z"
                          className="fill-white stroke-white"
                          strokeWidth="0.1"
                        />
                        <path
                          d="M4.11277 19.7684L4.11312 19.7688C4.58726 20.2429 5.10121 20.6779 5.69436 21.0734C7.47388 22.3386 9.6484 23.05 12.0197 23.05C14.3918 23.05 16.5662 22.2988 18.345 21.0734L18.3457 21.0729C18.8989 20.6778 19.4122 20.2434 19.9248 19.7702L19.9248 19.7702L19.9266 19.7685C21.9033 17.7522 23.0894 15.0242 23.0894 12.0197C23.0894 5.89188 18.1475 0.95 12.0197 0.95C5.89189 0.95 0.95 5.89188 0.95 12.0197C0.95 15.0244 2.17556 17.7917 4.11277 19.7684ZM12.0197 21.4151C9.68281 21.4151 7.57849 20.5627 5.93767 19.1658C6.37388 17.7853 7.64606 16.7924 9.14669 16.7924H14.932C16.4325 16.7924 17.7046 17.7852 18.1409 19.1655C16.461 20.5627 14.3565 21.4151 12.0197 21.4151ZM9.14669 15.1182C7.18756 15.1182 5.49857 16.2382 4.68909 17.8317C3.38792 16.2362 2.62424 14.2235 2.62424 12.0197C2.62424 6.8523 6.8523 2.62424 12.0197 2.62424C17.1871 2.62424 21.4151 6.8523 21.4151 12.0197C21.4151 14.2231 20.6517 16.2356 19.3886 17.8313C18.5416 16.2005 16.8528 15.1182 14.932 15.1182H9.14669Z"
                          className="fill-white stroke-white"
                          strokeWidth="0.1"
                        />
                      </svg>
                      <span className="ml-2">Profile</span>
                    </div>
                  </MobileNavLink>
                  {page.props.jetstream.hasApiFeatures ? (
                    <MobileNavLink
                      href={route('api-tokens.index')}
                      active={route().current('api-tokens.index')}
                    >
                      <div className="flex items-center">
                        <svg
                          width="5%"
                          viewBox="0 0 256 256"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect fill="none" />
                          <line
                            fill="none"
                            stroke="#FFF"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="16"
                            x1="96"
                            x2="96"
                            y1="56"
                            y2="200"
                          />
                          <path
                            d="M24,167.2a7.9,7.9,0,0,1,6.4-7.8,32.1,32.1,0,0,0,0-62.8A7.9,7.9,0,0,1,24,88.8V64a8,8,0,0,1,8-8H224a8,8,0,0,1,8,8V88.8a7.9,7.9,0,0,1-6.4,7.8,32.1,32.1,0,0,0,0,62.8,7.9,7.9,0,0,1,6.4,7.8V192a8,8,0,0,1-8,8H32a8,8,0,0,1-8-8Z"
                            fill="none"
                            stroke="#FFF"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="16"
                          />
                        </svg>
                        <span className="ml-2">API Tokens</span>
                      </div>
                    </MobileNavLink>
                  ) : null}
                  {/* <!-- Authentication --> */}
                  <form method="POST" onSubmit={logout}>
                    <button
                      type="submit"
                      className="w-full bg-grey-800 px-3 py-3 opacity-70 duration-mini hover:bg-grey-700 hover:opacity-100"
                    >
                      <div className="flex items-center hover:cursor-pointer">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 17 22"
                          fill="fill-dark"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.1455 5.36658C13.1455 2.88037 11.0339 0.8 8.47273 0.8C5.96449 0.8 3.85455 2.88194 3.85455 5.36658V7.80809H3.29091C1.92506 7.80809 0.8 8.92274 0.8 10.3261V18.7358C0.8 20.0884 1.92813 21.2 3.29091 21.2H13.7091C15.0719 21.2 16.2 20.0884 16.2 18.7358V10.3261C16.2 8.92274 15.0749 7.80809 13.7091 7.80809H13.1455V5.36658ZM11.6545 5.36658V7.80809H5.34545V5.36658C5.34545 3.70027 6.78096 2.27817 8.47273 2.27817C10.2212 2.27817 11.6545 3.70236 11.6545 5.36658ZM3.29091 9.28625H13.7091C14.2496 9.28625 14.7091 9.73219 14.7091 10.3261V18.7358C14.7091 19.2701 14.2554 19.7218 13.7091 19.7218H3.29091C2.7446 19.7218 2.29091 19.2701 2.29091 18.7358V10.3261C2.29091 9.73219 2.75039 9.28625 3.29091 9.28625Z"
                            className="fill-light stroke-light"
                            strokeWidth="0.4"
                          />
                          <path
                            d="M6.85941 13.4723L6.8 13.5311V13.6146C6.8 14.2971 7.17873 14.8779 7.78182 15.1508V16.3639C7.78182 16.5508 7.85648 16.7317 7.97463 16.8664C8.09333 17.0018 8.26777 17.103 8.47273 17.103C8.92109 17.103 9.21818 16.733 9.21818 16.3639V15.1462C9.77015 14.875 10.2 14.3025 10.2 13.6146C10.2 12.6886 9.44886 11.9591 8.47273 11.9591L8.46754 11.9591C8.36968 11.9591 8.28896 11.9591 8.22529 11.9621C8.16288 11.9652 8.09609 11.9716 8.03504 11.9943C7.89242 12.0471 7.83931 12.1682 7.8067 12.2425C7.80416 12.2483 7.80174 12.2538 7.79941 12.259C7.79849 12.261 7.79756 12.2631 7.79662 12.2652C7.72415 12.4265 7.56965 12.7704 6.85941 13.4723Z"
                            className="fill-light stroke-light"
                            strokeWidth="0.4"
                          />
                        </svg>
                        <span className="ml-2 text-lg text-light">Logout</span>
                      </div>
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default HeaderLayout;
